import { createContext, useContext } from 'react';

import type { EmptyStateContent } from '../types/command';

/**
 * Resolver for a main-mode empty-state (no scope entered, query has
 * no matches) or a main-mode loading-state (no scope entered,
 * something in the registry is `status: 'pending'`).
 *
 * Returning `undefined` falls through to the palette's generic
 * built-in copy -- consumers opt into custom copy granularly
 * (e.g. only render bespoke copy when the query is non-empty).
 */
export type MainEmptyStateResolver = (ctx: { query: string }) => EmptyStateContent | undefined;

export interface CommandPaletteEmptyStatesContextValue {
  /**
   * Returns copy for the main-mode "no commands matched the query"
   * case. The command-level `emptyState` field is only consulted
   * when the user has entered a root; this hook is the only way to
   * contribute consumer copy to the main-mode listbox.
   */
  mainEmptyState?: MainEmptyStateResolver;
  /**
   * Returns copy for the main-mode "something is loading and we
   * shouldn't claim 'no matches' yet" case. Fires when any command
   * in the registry has `status: 'pending'`. Returning `undefined`
   * falls back to the palette's generic "Searching…" message.
   */
  mainLoadingState?: MainEmptyStateResolver;
}

/**
 * Carries the main-mode empty/loading-state resolvers from the
 * provider down to the results component. Defaults to an empty
 * object -- the palette renders its built-in copy when no resolvers
 * are supplied. Held in its own context (rather than co-located with
 * the registry or telemetry contexts) because the resolvers are
 * set-once at provider mount and never change; coupling would force
 * unrelated subscribers to re-render on the (in practice nonexistent)
 * resolver swap.
 */
export const CommandPaletteEmptyStatesContext = createContext<CommandPaletteEmptyStatesContextValue>({});

/**
 * Read the active main-mode empty/loading-state resolvers.
 * Convention wrapper that matches the rest of the palette's context
 * accessors (`useCommandStrategy`, `useSnapPrefix`, etc).
 */
export function useMainEmptyStates(): CommandPaletteEmptyStatesContextValue {
  return useContext(CommandPaletteEmptyStatesContext);
}
